"use client";

import { PORTAL_ROUTES } from "@/lib/routing/portal-routes";

export type NavItem = {
  label: string;
  href: string;
  isSettings?: boolean;
  isBookings?: boolean;
  disabled?: boolean;
};

interface VenueNavItemsConfig {
  venueId: string;
  portalBase: "superadmin" | "booking" | "barstaff";
}

export function getVenueNavItems({ venueId, portalBase }: VenueNavItemsConfig) {
  switch (portalBase) {
    case "superadmin":
      return {
        primaryItems: [
          {
            label: "Dashboard",
            href: PORTAL_ROUTES.superadmin.venue(venueId).dashboard,
          },
          { label: "Bookings", href: "#", isBookings: true },
          {
            label: "Invoices",
            href: PORTAL_ROUTES.superadmin.venue(venueId).invoices,
          },
          {
            label: "Partners",
            href: PORTAL_ROUTES.superadmin.venue(venueId).partners,
          },
          { label: "Settings", href: "#", isSettings: true },
        ] as NavItem[],
        bookingsItems: [
          {
            label: "Calendar view",
            href: PORTAL_ROUTES.superadmin.venue(venueId).bookings,
          },
          {
            label: "Add new booking",
            href: `${PORTAL_ROUTES.superadmin.venue(venueId).base}/booking-new`,
          },
        ],
        settingsItems: [
          {
            label: "Studios",
            href: PORTAL_ROUTES.superadmin.venue(venueId).studios,
          },
          {
            label: "Kits",
            href: `${PORTAL_ROUTES.superadmin.venue(venueId).base}/kits`,
          },
          {
            label: "Shows",
            href: PORTAL_ROUTES.superadmin.venue(venueId).shows,
          },
          {
            label: "Price modifiers",
            href: PORTAL_ROUTES.superadmin.venue(venueId).priceModifiers,
          },
          {
            label: "Holidays",
            href: PORTAL_ROUTES.superadmin.venue(venueId).holidays,
          },
          {
            label: "Add-ons",
            href: PORTAL_ROUTES.superadmin.venue(venueId).addOns,
          },
          {
            label: "Shop items",
            href: PORTAL_ROUTES.superadmin.venue(venueId).shopItems,
          },
          { label: "FAQs", href: PORTAL_ROUTES.superadmin.venue(venueId).faqs },
          // Road shows are managed from the Shows page (pick "Road show" as the
          // type), so the standalone Road show menu item was removed.
          // {
          //   label: "Other Settings",
          //   href: `${PORTAL_ROUTES.superadmin.venue(venueId).base}/settings`,
          // },
        ],
      };

    case "booking":
      return {
        primaryItems: [
          {
            label: "Dashboard",
            href: PORTAL_ROUTES.booking.venue(venueId).dashboard,
          },
          { label: "Bookings", href: "#", isBookings: true },
          {
            label: "Invoices",
            href: PORTAL_ROUTES.booking.venue(venueId).invoices,
          },
          {
            label: "Partners",
            href: `${PORTAL_ROUTES.booking.venue(venueId).base}/partners`,
          },
          { label: "Settings", href: "#", isSettings: true },
        ] as NavItem[],
        bookingsItems: [
          {
            label: "Calendar view",
            href: PORTAL_ROUTES.booking.venue(venueId).bookings,
          },
          {
            label: "Add new booking",
            href: `${PORTAL_ROUTES.booking.venue(venueId).base}/booking-new`,
          },
        ],
        settingsItems: [
          {
            label: "Studios",
            href: PORTAL_ROUTES.booking.venue(venueId).studios,
          },
          {
            label: "Kits",
            href: `${PORTAL_ROUTES.booking.venue(venueId).base}/kits`,
          },
          { label: "Shows", href: PORTAL_ROUTES.booking.venue(venueId).shows },
          {
            label: "Price modifiers",
            href: PORTAL_ROUTES.booking.venue(venueId).priceModifiers,
          },
          {
            label: "Holidays",
            href: PORTAL_ROUTES.booking.venue(venueId).holidays,
          },
          {
            label: "Add-ons",
            href: PORTAL_ROUTES.booking.venue(venueId).addOns,
          },
          {
            label: "Shop items",
            href: PORTAL_ROUTES.booking.venue(venueId).shopItems,
          },
          { label: "FAQs", href: PORTAL_ROUTES.booking.venue(venueId).faqs },
          {
            label: "Venue settings",
            href: `${PORTAL_ROUTES.booking.venue(venueId).base}/settings`,
          },
          // Road shows are managed from the Shows page (pick "Road show" as the
          // type), so the standalone Road show menu item was removed.
        ],
      };

    case "barstaff":
      return {
        primaryItems: [
          { label: "Kiosk", href: PORTAL_ROUTES.barstaff.venue(venueId).kiosk },
          {
            label: "Today's bookings",
            href: PORTAL_ROUTES.barstaff.venue(venueId).todaysBookings,
          },
          {
            label: "Quick booking",
            href: PORTAL_ROUTES.barstaff.venue(venueId).booking,
          },
        ] as NavItem[],
        bookingsItems: [],
        settingsItems: [],
      };

    default:
      return { primaryItems: [], bookingsItems: [], settingsItems: [] };
  }
}

export function isNavItemActive(pathname: string, href: string): boolean {
  if (href === "#") return false;
  return pathname === href || pathname.startsWith(href + "/");
}
